import { Button, ButtonGroup } from "@msuite/ui/button";
import { TextInput } from "@msuite/ui/form";
import { useDisclosure, useFilter, useSearchForm } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import type { VirtuosoHandles } from "@msuite/ui/libs/virtuoso";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { cn, responseHandler } from "@msuite/ui/utils";
import React from "react";
import { client } from "@/hono";
import { useTicketRules } from "@/hooks/use-ticket-rules";
import { AddTicketModal } from "@/modals/projects/add-ticket-modal";
import { ProjectActivityGraph } from "./project-activity-graph";
import { ProjectTicketsStateContainer } from "./project-tickets-state-container";

/** Props Interface */
interface ProjectTicketsProps {
	projectId: string;
	virtuosoHandles: VirtuosoHandles;
}

export function ProjectTickets({ projectId, virtuosoHandles }: ProjectTicketsProps) {
	/** Hooks */
	const addTicketModal = useDisclosure();
	const search = useDisclosure();
	const { t } = useTranslation();
	const methods = useSearchForm("project-tickets");
	const { createIsDenied } = useTicketRules();

	/** Query */
	const { data: _tickets } = useQuery({
		queryKey: ["tickets", projectId],
		queryFn: async () => {
			const response = await client.projects[":projectId"].tickets.$get({
				param: {
					projectId,
				},
				query: {
					state: ["active", "inactive", "archived"],
				},
			});
			const data = await responseHandler(response);
			return data.tickets;
		},
		staleTime: 1000,
	});

	const { filtered: tickets } = useFilter(_tickets ?? [], methods.searchValue);

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack">
				<p className="heading">{t("projects.tickets")}</p>
				<div className="flex-1" />
				<ButtonGroup isAttached={false}>
					<Button
						icon={
							<Icon
								name="Search"
								className={cn(search.isOpen && "stroke-accent-500")}
							/>
						}
						onClick={search.onToggle}
					/>
					{!createIsDenied(projectId) && (
						<Button
							icon={<Icon name="Plus" />}
							onClick={() => {
								addTicketModal.onOpen();
							}}
							data-cy="projects-add-ticket-button"
						>
							{t("projects.add_ticket")}
						</Button>
					)}
				</ButtonGroup>
			</div>
			{search.isOpen && (
				<div className="pl-0.5">
					<TextInput
						type="search"
						control={methods.control}
						name="searchValue"
						isRequired={false}
						label={undefined}
						placeholder={t("general.search")}
					/>
				</div>
			)}
			<div className="scrollbar-hidden bottom-space-2xl flex flex-col gap-2 overflow-y-auto">
				<ProjectActivityGraph
					projectId={projectId}
					virtuosoHandles={virtuosoHandles}
				/>
				<ProjectTicketsStateContainer
					tickets={tickets}
					state="active"
					defaultIsOpen={true}
				/>
				<ProjectTicketsStateContainer
					tickets={tickets}
					state="inactive"
					defaultIsOpen={false}
				/>
				<ProjectTicketsStateContainer
					tickets={tickets}
					state="archived"
					defaultIsOpen={false}
				/>
			</div>
			{addTicketModal.isOpen && (
				<AddTicketModal
					isOpen={addTicketModal.isOpen}
					onClose={addTicketModal.onClose}
					projectId={projectId}
				/>
			)}
		</React.Fragment>
	);
}
